import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { format } from 'vscode/vscode/vs/base/common/strings';
import { IKeybindingService } from 'vscode/vscode/vs/platform/keybinding/common/keybinding.service';
import { IAccessibleViewService } from 'vscode/vscode/vs/workbench/contrib/accessibility/browser/accessibleView.service';
import { getNotebookEditorFromEditorPane } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/notebookBrowser';

const _moduleId = "vs/workbench/contrib/notebook/browser/notebookAccessibility";
function getAccessibilityHelpText(accessor) {
    const keybindingService = accessor.get(IKeybindingService);
    const content = [];
    content.push(( localizeWithPath(
        _moduleId,
        0,
        'The notebook view is a collection of code and markdown cells. Code cells can be executed and will produce output directly below the cell.'
    )));
    content.push(descriptionForCommand('notebook.cell.edit', ( localizeWithPath(_moduleId, 1, 'The Edit Cell command ({0}) will focus on the cell input.')), ( localizeWithPath(
        _moduleId,
        2,
        'The Edit Cell command will focus on the cell input and is currently not triggerable by a keybinding.'
    )), keybindingService));
    content.push(descriptionForCommand('notebook.cell.quitEdit', ( localizeWithPath(
        _moduleId,
        3,
        'The Quit Edit command ({0}) will set focus on the cell container. The default (Escape) key may need to be pressed twice first exit the virtual cursor if active.'
    )), ( localizeWithPath(
        _moduleId,
        4,
        'The Quit Edit command will set focus on the cell container and is currently not triggerable by a keybinding.'
    )), keybindingService));
    content.push(descriptionForCommand('notebook.cell.focusInOutput', ( localizeWithPath(
        _moduleId,
        5,
        'The Focus Output command ({0}) will set focus in the cell\'s output.'
    )), ( localizeWithPath(
        _moduleId,
        6,
        'The Quit Edit command will set focus in the cell\'s output and is currently not triggerable by a keybinding.'
    )), keybindingService));
    content.push(descriptionForCommand('notebook.focusNextEditor', ( localizeWithPath(
        _moduleId,
        7,
        'The Focus Next Cell Editor command ({0}) will set focus in the next cell\'s editor.'
    )), ( localizeWithPath(
        _moduleId,
        8,
        'The Focus Next Cell Editor command will set focus in the next cell\'s editor and is currently not triggerable by a keybinding.'
    )), keybindingService));
    content.push(descriptionForCommand('notebook.focusPreviousEditor', ( localizeWithPath(
        _moduleId,
        9,
        'The Focus Previous Cell Editor command ({0}) will set focus in the previous cell\'s editor.'
    )), ( localizeWithPath(
        _moduleId,
        10,
        'The Focus Previous Cell Editor command will set focus in the previous cell\'s editor and is currently not triggerable by a keybinding.'
    )), keybindingService));
    content.push(( localizeWithPath(
        _moduleId,
        11,
        'The up and down arrows will also move focus between cells while focused on the outer cell container.'
    )));
    content.push(descriptionForCommand('notebook.cell.executeAndFocusContainer', ( localizeWithPath(
        _moduleId,
        12,
        'The Execute Cell command ({0}) executes the cell that currently has focus.'
    )), ( localizeWithPath(
        _moduleId,
        13,
        'The Execute Cell command executes the cell that currently has focus and is currently not triggerable by a keybinding.'
    )), keybindingService));
    content.push(( localizeWithPath(
        _moduleId,
        14,
        'The Insert Cell Above/Below commands will create new empty code cells'
    )));
    content.push(( localizeWithPath(
        _moduleId,
        15,
        'The Change Cell to Code/Markdown commands are used to switch between cell types.'
    )));
    return content.join('\n\n');
}
function descriptionForCommand(commandId, msg, noKbMsg, keybindingService) {
    const kb = keybindingService.lookupKeybinding(commandId);
    if (kb) {
        return format(msg, kb.getAriaLabel());
    }
    return format(noKbMsg, commandId);
}
async function runAccessibilityHelpAction(accessor, editor) {
    const accessibleViewService = accessor.get(IAccessibleViewService);
    const helpText = getAccessibilityHelpText(accessor);
    accessibleViewService.show({
        id: "notebook" ,
        verbositySettingKey: "accessibility.verbosity.notebook" ,
        provideContent: () => helpText,
        onClose: () => {
            editor.focus();
        },
        options: { type: "help"  }
    });
}
function showAccessibleOutput(accessibleViewService, editorService) {
    const activePane = editorService.activeEditorPane;
    const notebookEditor = getNotebookEditorFromEditorPane(activePane);
    const notebookViewModel = notebookEditor?.getViewModel();
    const selections = notebookViewModel?.getSelections();
    const notebookDocument = notebookViewModel?.notebookDocument;
    if (!selections || !notebookDocument || !notebookEditor?.textModel) {
        return false;
    }
    const viewCell = notebookViewModel.viewCells[selections[0].start];
    let outputContent = '';
    const decoder = ( (new TextDecoder()));
    for (let i = 0; i < viewCell.outputsViewModels.length; i++) {
        const outputViewModel = viewCell.outputsViewModels[i];
        const outputTextModel = viewCell.model.outputs[i];
        const [mimeTypes, pick] = outputViewModel.resolveMimeTypes(notebookEditor.textModel, undefined);
        const mimeType = mimeTypes[pick].mimeType;
        let buffer = outputTextModel.outputs.find(output => output.mime === mimeType);
        if (!buffer || mimeType.startsWith('image')) {
            buffer = outputTextModel.outputs.find(output => !output.mime.startsWith('image'));
        }
        let text = `${mimeType}`;
        if (buffer) {
            const charLimit = 100_000;
            text = decoder.decode(buffer.data.slice(0, charLimit).buffer);
            if (buffer.data.byteLength > charLimit) {
                text = text + '...(truncated)';
            }
            if (mimeType.endsWith('error')) {
                text = text.replace(/\\u001b\[[0-9;]*m/gi, '').replaceAll('\\n', '\n');
            }
        }
        const index = viewCell.outputsViewModels.length > 1
            ? `Cell output ${i + 1} of ${viewCell.outputsViewModels.length}\n`
            : '';
        outputContent = outputContent.concat(`${index}${text}\n`);
    }
    if (!outputContent) {
        return false;
    }
    accessibleViewService.show({
        id: "notebook" ,
        verbositySettingKey: "accessibility.verbosity.notebook" ,
        provideContent() { return outputContent; },
        onClose() {
            notebookEditor?.setFocus(selections[0]);
            activePane?.focus();
        },
        options: { type: "view"  }
    });
    return true;
}

export { getAccessibilityHelpText, runAccessibilityHelpAction, showAccessibleOutput };
